/**
 * Production-only recovery for stale chunk / deployment mismatch load failures.
 */

import { shouldInstallProductionHandlers } from "@/bootstrap/production-handler-gate";
import { renderChunkUpdateShell } from "@/theme/startup-safe-render";

let installed = false;
let shown = false;

export function isChunkLoadFailure(reason: unknown): boolean {
  const name =
    reason && typeof reason === "object" && "name" in reason
      ? String((reason as { name?: unknown }).name ?? "")
      : "";
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : reason && typeof reason === "object" && "message" in reason
          ? String((reason as { message?: unknown }).message ?? "")
          : String(reason ?? "");

  const haystack = `${name} ${message}`.toLowerCase();
  return (
    name === "ChunkLoadError" ||
    haystack.includes("chunkloaderror") ||
    haystack.includes("loading chunk") ||
    haystack.includes("failed to fetch dynamically imported module") ||
    haystack.includes("importing a module script failed")
  );
}

function safePaintChunkShell(): void {
  if (shown) return;
  shown = true;
  try {
    renderChunkUpdateShell();
  } catch (paintError) {
    // eslint-disable-next-line no-console -- chunk recovery containment
    console.error("[chunk] failed to paint update shell", paintError);
  }
}

function onWindowError(event: ErrorEvent): void {
  try {
    if (!isChunkLoadFailure(event.error ?? event.message)) return;
    // eslint-disable-next-line no-console -- chunk recovery
    console.error("[chunk] load failure", event.error ?? event.message);
    safePaintChunkShell();
  } catch {
    // never throw from handler
  }
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  try {
    if (!isChunkLoadFailure(event.reason)) return;
    // eslint-disable-next-line no-console -- chunk recovery
    console.error("[chunk] load failure (rejection)", event.reason);
    safePaintChunkShell();
  } catch {
    // never throw from handler
  }
}

/** Register production-only chunk mismatch listeners. Idempotent. */
export function installChunkErrorHandler(): void {
  if (!shouldInstallProductionHandlers()) return;
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;

  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
}

/** Test-only reset for Jest isolation. */
export function resetChunkErrorHandlerForTests(): void {
  if (typeof window !== "undefined" && installed) {
    window.removeEventListener("error", onWindowError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }
  installed = false;
  shown = false;
}
